<template>
  <div>
    <b-modal
      v-model="show"
      :title="$t('Add Service Order')"
      modal-class="modal-primary"
      @hidden="close"
      size="xl"
      no-close-on-backdrop
      scrollable
    >
      <b-card :bg-variant="isDarkSkin ? 'black' : 'white'" class="mb-1">
        <ValidationObserver ref="form-add-service-order">
          <b-row>
            <b-col md="3">
              <ValidationProvider
                v-slot="{ errors }"
                name="date"
                rules="required"
              >
                <b-form-group
                  :label="$t('Date')"
                  label-for="date"
                  :state="errors[0] ? false : null"
                >
                  <kendo-datepicker
                    v-model="serviceOrder.date"
                    v-mask="'##/##/####'"
                    class="form-control-kendo"
                    :class="[
                      isDarkSkin ? ' bg-transparent' : '',
                      errors[0] ? 'border-danger' : '',
                    ]"
                    :format="'dd/MM/yyyy'"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col md="6">
              <ValidationProvider
                v-slot="{ errors }"
                name="client"
                rules="required"
              >
                <b-form-group
                  :label="$t('Client')"
                  label-for="client"
                  :state="errors[0] ? false : null"
                >
                  <v-select
                    :clearable="false"
                    class="rounded"
                    v-model="serviceOrder.client_id"
                    :placeholder="$t('Select a client')"
                    :class="errors[0] ? 'border-danger' : ''"
                    :reduce="(value) => value.id"
                    label="name"
                    :options="clients"
                    autofocus
                    @input="getServiceTypesForSelect()"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col md="3">
              <ValidationProvider
                v-slot="{ errors }"
                name="serviceType"
                rules="required"
              >
                <b-form-group
                  :label="$t('Service Type')"
                  label-for="serviceType"
                  :state="errors[0] ? false : null"
                >
                  <v-select
                    :clearable="false"
                    class="rounded"
                    v-model="serviceOrder.serviceType"
                    :placeholder="$t('Select a service type')"
                    :class="errors[0] ? 'border-danger' : ''"
                    :reduce="(value) => value.id"
                    label="name"
                    :options="service_types"
                    autofocus
                    :disabled="emptyClient"
                    @input="getVehicleSettingsByClientAndServiceType()"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col md="3">
              <ValidationProvider
                v-slot="{ errors }"
                name="vehicleSetting"
                rules="required"
              >
                <b-form-group
                  :label="$t('Vehicle Setting')"
                  label-for="vehicleSetting"
                  :state="errors[0] ? false : null"
                >
                  <v-select
                    :clearable="false"
                    class="rounded"
                    v-model="serviceOrder.vehicleSetting"
                    :placeholder="$t('Select a vehicle setting')"
                    :class="errors[0] ? 'border-danger' : ''"
                    :reduce="(value) => value.setting_vehicle_id"
                    label="code"
                    :options="vehicle_settings"
                    autofocus
                    :disabled="emptyServiceTypes || emptyClient"
                    @input="getMeasureTypesByClientAndServiceType()"
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col md="3">
              <ValidationProvider
                v-slot="{ errors }"
                name="measureType"
                rules="required"
              >
                <b-form-group
                  :label="$t('Measure Type')"
                  label-for="measureType"
                  :state="errors[0] ? false : null"
                >
                  <v-select
                    :clearable="false"
                    class="rounded"
                    v-model="serviceOrder.measureType"
                    :placeholder="$t('Select a measure type')"
                    :class="errors[0] ? 'border-danger' : ''"
                    :reduce="(value) => value.id"
                    label="name"
                    :options="measure_types"
                    autofocus
                    :disabled="emptyServiceTypes || emptyClient"
                    @input="
                      getRatesByClientServiceTypeSettingVehicleAndMeasureType()
                    "
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col md="6">
              <ValidationProvider
                v-slot="{ errors }"
                name="rate"
                rules="required"
              >
                <b-form-group
                  :label="$t('Route')"
                  label-for="rate"
                  :state="errors[0] ? false : null"
                >
                  <v-select
                    :clearable="false"
                    class="rounded"
                    v-model="serviceOrder.rate_id"
                    :placeholder="$t('Select a rate')"
                    :class="errors[0] ? 'border-danger' : ''"
                    :reduce="(value) => value.id"
                    label="route"
                    :options="rates"
                    autofocus
                    :disabled="
                      emptyServiceTypes ||
                      emptyClient ||
                      emptyVehicleSettings ||
                      emptyMeasureTypes
                    "
                  />
                </b-form-group>
              </ValidationProvider>
            </b-col>
          </b-row>
        </ValidationObserver>
      </b-card>

      <b-card
        :bg-variant="isDarkSkin ? 'black' : 'white'"
        title-tag="h5"
        class="mb-1"
      >
        <b-card-title>
          <div class="d-flex justify-content-between">
            <h4>{{ $t("Documents") }}</h4>
            <tabler-icon
              v-b-tooltip.hover.v-primary
              :title="$t('Add services')"
              icon="PlusIcon"
              class="text-primary cursor-pointer"
              size="25"
              @click="openAddDocumentOrdersModal"
            />
          </div>
        </b-card-title>

        <b-table
          hover
          small
          responsive
          class="font-small-3"
          table-class="text-nowrap"
          sticky-header="30vh"
          :fields="documentsFields"
          :items="serviceOrder.documents"
          show-empty
          :empty-text="$t('No matching records found')"
        >
          <template #table-busy>
            <b-skeleton-table
              :headers="fields"
              :rows="paginate.perPage"
              :columns="fields.length"
              :table-props="{ bordered: true, striped: true }"
            />
          </template>

          <template #cell(load_date)="data">
            <div class="d-flex flex-column">
              <div class="d-flex">
                <div class="text-right mr-1" style="width: 80px">
                  <span> {{ $t("Load") }} </span>
                </div>
                <feather-icon
                  icon="CalendarIcon"
                  size="17"
                  class="cursor-pointer text-warning"
                />
                <span style="margin-left: 5px">
                  {{ data.item.load_date | myGlobal }}
                </span>
              </div>
              <div class="d-flex justify-content-center">
                <th class="p-0" style="margin-top: 5px; width: 150px" />
              </div>
              <div class="d-flex" style="margin-top: 5px">
                <div class="text-right mr-1" style="width: 80px">
                  <span> {{ $t("Delivery") }} </span>
                </div>
                <feather-icon
                  icon="CalendarIcon"
                  size="17"
                  class="cursor-pointer text-primary"
                />
                <span style="margin-left: 5px">
                  {{ data.item.required_delivery_date | myGlobal }}
                </span>
              </div>
            </div>
          </template>

          <template #cell(transport_number)="data">
            <span class="">{{ data.item.transport_number }}</span>
          </template>

          <template #cell(service_order_code)="data">
            <span class="">{{ data.item.service_order_code }}</span>
          </template>

          <template #cell(remittance_number)="data">
            <span> {{ data.item.remittance_number }} </span>
          </template>

          <template #cell(quantity)="data">
            <div
              class="d-flex align-items-left justify-content-left flex-column"
            >
              <div class="mt-05" v-if="data.item.quantity">
                <span class="text-success">{{
                  data.item.quantity == null ? 0 : data.item.quantity
                }}</span>
              </div>
              <span v-else>-</span>
            </div>
          </template>

          <template #cell(weight)="data">
            <div
              class="d-flex align-items-left justify-content-left flex-column"
            >
              <div class="mt-05" v-if="data.item.weight">
                <span class="text-info">{{ data.item.weight.toFixed(3) }}</span>
              </div>
              <span v-else>-</span>
            </div>
          </template>

          <template #cell(volume)="data">
            <div
              class="d-flex align-items-left justify-content-left flex-column"
            >
              <div class="mt-05" v-if="data.item.volume">
                <span class="text-primary">{{
                  data.item.volume.toFixed(3)
                }}</span>
              </div>
              <span v-else>-</span>
            </div>
          </template>

          <template #cell(created_at)="data">
            <div
              class="d-flex align-items-left justify-content-left flex-column"
            >
              <div class="mt-05" v-if="data.item.required_delivery_date">
                <tabler-icon
                  icon="TruckDeliveryIcon"
                  size="17"
                  class="text-primary cursor-pointer mr-1"
                  v-b-tooltip.hover.left="$t('DELIVERY DATE')"
                />
                <span class="text-primary">{{
                  data.item.required_delivery_date
                }}</span>
              </div>
              <div class="mt-05" v-if="data.item.created_at">
                <tabler-icon
                  icon="ClockIcon"
                  size="17"
                  class="text-info cursor-pointer mr-1"
                  v-b-tooltip.hover.left="$t('CREATED AT')"
                />

                <span class="text-info">{{
                  data.item.created_at | prettyDate
                }}</span>
              </div>
              <div class="mt-05" v-if="data.item.created_by">
                <tabler-icon
                  icon="UserIcon"
                  size="17"
                  class="text-primary cursor-pointer mr-1"
                  v-b-tooltip.hover.left="$t('CREATED BY')"
                />

                <span class="text-primary">{{ data.item.created_by }}</span>
              </div>
            </div>
          </template>

          <template #cell(actions)="data">
            <!-- <div class="d-flex justify-content-center">
                    <feather-icon
                      icon="TrashIcon"
                      size="18"
                      class="text-danger cursor-pointer"
                      @click="removeDocument(data.item)"
                    />
                  </div> -->
            <feather-icon
              v-if="!data.item.is_delete"
              icon="TrashIcon"
              class="text-danger"
              v-b-tooltip.hover.v-danger
              :title="$t('Remove document')"
              @click="deleteDocument(data.item)"
            />
            <feather-icon
              v-else
              icon="RotateCcwIcon"
              class="text-success cursor-pointer"
              v-b-tooltip.hover.v-success
              :title="$t('Reverse document')"
              @click="data.item.is_delete = false"
            />
          </template>
        </b-table>
      </b-card>

      <b-card :bg-variant="isDarkSkin ? 'black' : 'white'" class="mb-0">
        <div class="d-flex justify-content-between">
          <div>
            {{ $t("Documents selected") }} :
            <span class="text-primary"> {{ getTotalSelected }} Items </span>
          </div>
          <div>
            {{ $t("Quantity") }} :
            <span class="text-info"> {{ getTotalQuantitySelected }} </span>
          </div>
          <div>
            {{ $t("Volume") }} :
            <span class="text-primary"> {{ getTotalVolumeSelected }} M3 </span>
          </div>
          <div>
            {{ $t("Weight") }} :
            <span class="text-warning"> {{ getTotalWeightSelected }} </span>
          </div>
        </div>
      </b-card>

      <template #modal-footer>
        <b-button variant="outline-danger" @click="close()">
          {{ $t("Cancel") }}
        </b-button>

        <b-button
          v-if="serviceId == null"
          variant="outline-success"
          @click="createServiceOrder"
          class="float-right"
        >
          {{ $t("Save") }}
        </b-button>

        <b-button
          v-if="serviceId != null"
          variant="outline-warning"
          @click="updateServiceOrder"
          class="float-right"
        >
          {{ $t("Update") }}
        </b-button>
      </template>
    </b-modal>

    <AddDocumentOrdersModal
      v-if="showAddDocumentOrdersModal"
      :currentOrders="currentDocuments"
      @add-document-orders="addDocumentOrders"
      @close="showAddDocumentOrdersModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import ServiceOrdersService from "@/views/operations/views/service-orders/services/service-orders.service.js";
import ClientService from "@/views/commons/clients/services/clients.service.js";
import TravelRoutesService from "@/views/settings/views/travel-routes/services/travel-routes.service.js";
import AddDocumentOrdersModal from "@/views/operations/views/service-orders/components/AddDocumentOrdersModal.vue";
import DocumentOrderFields from "@/views/operations/views/service-orders/data/document-orders.fields.js";

export default {
  name: "AddserviceOrder",
  components: {
    AddDocumentOrdersModal,
  },
  props: {
    serviceId: {
      default: null,
    },
  },
  data() {
    return {
      show: true,
      showAddDocumentOrdersModal: false,
      finishedLoading: false,
      serviceOrder: {
        id: null,
        date: null,
        travel_order_id: null,
        rate_id: null,
        serie: null,
        number: null,
        date: "",
        client_id: null,
        serviceType: "",
        vehicleSetting: "",
        measureType: "",
        created_by: "",
        documents: [],
      },
      clients: [],
      service_types: [],
      travel_orders: [],
      measure_types: [],
      vehicle_settings: [],
      rates: [],
      documents: [],
      documentsFields: [],
      currentDocuments: [],
      // lastDocumentRemoved: null,
      // showRetrieveRemovedDocument: false,
      // retrieveDocumentAutoHideDelay: 10000,
      // documentPosition: null,
      // retrieveDocumentProgressValue: 0,
      // intervalId: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),

    emptyServiceTypes() {
      return (
        this.serviceOrder.serviceType == null ||
        this.serviceOrder.serviceType == ""
      );
    },

    emptyClient() {
      return (
        this.serviceOrder.client_id == null || this.serviceOrder.client_id == ""
      );
    },

    emptyVehicleSettings() {
      return (
        this.serviceOrder.vehicleSetting == null ||
        this.serviceOrder.vehicleSetting == ""
      );
    },

    emptyMeasureTypes() {
      return (
        this.serviceOrder.measureType == null ||
        this.serviceOrder.measureType == ""
      );
    },

    getTotalSelected() {
      return this.serviceOrder.documents.length;
    },

    getTotalQuantitySelected() {
      let quantity = 0;
      this.serviceOrder.documents.map((service) => {
        quantity += service.quantity;
      });

      return quantity;
    },

    getTotalVolumeSelected() {
      let volume = 0;
      this.serviceOrder.documents.map((service) => {
        volume += service.volume;
      });
      return parseFloat(volume.toFixed(3));
    },

    getTotalWeightSelected() {
      let weight = 0;
      this.serviceOrder.documents.map((service) => {
        weight += service.weight;
      });
      return parseFloat(weight.toFixed(3));
    },
  },
  async created() {
    this.addPreloader();
    this.finishedLoading = false;
    try {
      this.serviceOrder.created_by = this.currentUser.id;
      await Promise.all([
        this.getDataServiceById(),
        this.getClientsForSelect(),
        this.setCustomDocumentFields(),
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      this.finishedLoading = true;
      this.removePreloader();
    }
  },
  methods: {
    async getDataServiceById() {
      this.serviceOrder.date = moment().format("DD/MM/YYYY");
      if (this.serviceId == null) return;
      try {
        const { data } = await ServiceOrdersService.getServiceOrderById({
          service_id: this.serviceId,
        });
        this.serviceOrder.date = moment(data.date).format("DD/MM/YYYY");
        this.serviceOrder.client_id = data.client_id;
        await this.getServiceTypesForSelect();
        this.serviceOrder.serviceType = data.service_type_id;
        await this.getVehicleSettingsByClientAndServiceType();
        this.serviceOrder.vehicleSetting = data.setting_vehicle_id;
        await this.getMeasureTypesByClientAndServiceType();
        this.serviceOrder.measureType = data.measure_type;
        await this.getRatesByClientServiceTypeSettingVehicleAndMeasureType();
        this.serviceOrder.rate_id = data.rate_id;
        this.serviceOrder.serie = data.serie;
        this.serviceOrder.number = data.number;
        this.serviceOrder.documents =
          data.documents == null ? [] : JSON.parse(data.documents);
      } catch (error) {
        throw error;
      }
    },

    deleteDocument(document) {
      if (document.service_id != null) {
        this.$set(document, "is_delete", true);
      } else {
        const filter = this.serviceOrder.documents.filter(
          (doo) => doo.document_id != document.document_id
        );
        this.serviceOrder.documents = filter;
      }
    },
    // retrieveDocument() {
    //   // this.documents.push(this.lastDocumentRemoved);
    //   this.documents.splice(this.documentPosition, 0, this.lastDocumentRemoved);
    //   this.lastDocumentRemoved = null;
    //   this.retrieveDocumentProgressValue = 0;
    //   this.showRetrieveRemovedDocument = false;
    // },
    // removeDocument(document) {
    //   // if (this.showRetrieveRemovedDocument) {
    //   //   this.showRetrieveRemovedDocument = false;
    //   //   this.lastDocumentRemoved = null;
    //   //   this.retrieveDocumentProgressValue = 0;
    //   // }

    //   if (this.intervalId) {
    //     clearInterval(this.intervalId);
    //     this.intervalId = null;
    //   }

    //   this.showRetrieveRemovedDocument = true;
    //   this.retrieveDocumentProgressValue = 0;
    //   this.lastDocumentRemoved = document;
    //   this.documentPosition = this.documents.indexOf(document);
    //   this.documents = this.documents.filter((doc) => doc !== document);

    //   this.intervalId = setInterval(() => {
    //     this.retrieveDocumentProgressValue += 100;
    //     if (
    //       this.retrieveDocumentProgressValue >=
    //       this.retrieveDocumentAutoHideDelay
    //     ) {
    //       clearInterval(this.intervalId);
    //       this.showRetrieveRemovedDocument = false;
    //       this.intervalId = null;
    //     }

    //     if (
    //       this.retrieveDocumentProgressValue ===
    //       this.retrieveDocumentAutoHideDelay
    //     ) {
    //       this.showRetrieveRemovedDocument = false;
    //     }
    //   }, 100);
    // },

    setCustomDocumentFields() {
      // copy fields from document orders
      this.documentsFields = [...DocumentOrderFields];
      // remove check column
      this.documentsFields.shift();
      // add to the end actions column
      this.documentsFields.push({
        key: "actions",
        label: this.$t("Actions"),
        sortable: false,
      });
    },

    addDocumentOrders(documents) {
      documents.map((document) => {
        const isExist = this.serviceOrder.documents.find(
          (serv) => serv.document_id == document.document_id
        );
        if (!isExist) this.serviceOrder.documents.push(document);
      });
      this.showAddDocumentOrdersModal = false;
    },

    close() {
      this.$emit("close");
    },

    openAddDocumentOrdersModal() {
      const currentDocuments = this.serviceOrder.documents.map((document) => {
        return document.document_id;
      });
      this.currentDocuments = currentDocuments;
      this.showAddDocumentOrdersModal = true;
    },

    clearSelects() {
      this.serviceOrder.serviceType = "";
      this.serviceOrder.vehicleSetting = "";
      this.serviceOrder.measureType = "";
      this.serviceOrder.rate_id = "";
      this.service_types = [];
      this.vehicle_settings = [];
      this.measure_types = [];
      this.rates = [];
    },

    // 1 list clients
    async getClientsForSelect() {
      try {
        const { data } = await ClientService.getClientsForSelect();
        this.clients = data;
      } catch (error) {
        this.showToastError();
      }
    },

    // 2 list service types by client
    async getServiceTypesForSelect() {
      try {
        this.clearSelects();
        const { data } = await ClientService.getServiceTypesForSelect({
          client_id: this.serviceOrder.client_id,
        });
        this.service_types = data;
      } catch (error) {
        console.log(error);

        this.showToastError();
      }
    },

    // 3 list vehicle settings by client and service type
    async getVehicleSettingsByClientAndServiceType() {
      this.vehicle_settings = [];
      this.measure_types = [];
      this.rates = [];
      this.serviceOrder.vehicleSetting = null;
      this.serviceOrder.measureType = null;
      this.serviceOrder.rate_id = null;
      try {
        const { data } =
          await TravelRoutesService.getVehicleSettingsByClientAndServiceType({
            client_id: this.serviceOrder.client_id,
            service_type_id: this.serviceOrder.serviceType,
          });

        this.vehicle_settings = data;
      } catch (error) {
        console.log(error);
        this.showToastError();
      }
    },

    // 4 list measure types by client and service type
    async getMeasureTypesByClientAndServiceType() {
      this.measure_types = [];
      this.rates = [];
      this.serviceOrder.measureType = null;
      this.serviceOrder.rate_id = null;
      try {
        const { data } =
          await TravelRoutesService.getMeasureTypesByClientAndServiceType({
            client_id: this.serviceOrder.client_id,
            service_type_id: this.serviceOrder.serviceType,
            setting_vehicle_id: this.serviceOrder.vehicleSetting,
          });

        this.measure_types = data;
      } catch (error) {
        console.log(error);
        this.showToastError();
      }
    },

    // 5 list travel routes by client, service type, setting vehicle and measure type
    async getRatesByClientServiceTypeSettingVehicleAndMeasureType() {
      this.rates = [];
      this.serviceOrder.rate_id = null;
      try {
        if (
          this.emptyServiceTypes ||
          this.emptyClient ||
          this.emptyVehicleSettings ||
          this.emptyMeasureTypes
        ) {
          return;
        }

        const { data } =
          await TravelRoutesService.getRatesByClientServiceTypeSettingVehicleAndMeasureType(
            {
              client_id: this.serviceOrder.client_id,
              service_type_id: this.serviceOrder.serviceType,
              setting_vehicle_id: this.serviceOrder.vehicleSetting,
              measure_type: this.serviceOrder.measureType,
            }
          );

        this.rates = data;
      } catch (error) {
        console.log(error);
        this.showToastError();
      }
    },

    async createServiceOrder() {
      try {
        const isValid = await this.$refs["form-add-service-order"].validate();
        if (!isValid) return;

        const confirm = await this.showConfirmSwal();
        if (!confirm.isConfirmed) return;

        this.addPreloader();
        const documents = this.serviceOrder.documents.filter(
          (doc) => !doc.is_delete
        );
        const params = {
          date: this.serviceOrder.date,
          rate_id: this.serviceOrder.rate_id,
          documents: JSON.stringify(documents),
        };
        const { data } = await ServiceOrdersService.createServiceOrder(params);
        if (data.type == "success") {
          this.showToastSuccess(data.message);
          this.$emit("save");
        } else {
          this.showToastWarning(data.message);
        }
      } catch (error) {
        console.log(error);
        this.showToastError(error?.response?.data?.message ?? error?.response);
      } finally {
        this.removePreloader();
      }
    },

    async updateServiceOrder() {
      try {
        const isValid = await this.$refs["form-add-service-order"].validate();
        if (!isValid) return;

        const confirm = await this.showConfirmSwal();
        if (!confirm.isConfirmed) return;

        this.addPreloader();
        const documents = this.serviceOrder.documents.filter(
          (doc) => !doc.is_delete
        );
        const params = {
          service_id: this.serviceId,
          date: this.serviceOrder.date,
          rate_id: this.serviceOrder.rate_id,
          documents: JSON.stringify(documents),
        };
        const { data } = await ServiceOrdersService.updateServiceOrder(params);
        if (data.type == "success") {
          this.showToastSuccess(data.message);
          this.$emit("save");
        } else {
          this.showToastWarning(data.message);
        }
      } catch (error) {
        console.log(error);
        this.showToastError(error?.response?.data?.message ?? error?.response);
      } finally {
        this.removePreloader();
      }
    },
  },
};
</script>

<style></style>
